// @flow
import React, { type Node } from 'react';
import { getWeekDay } from '@tectonics/prs/week-day';
import { getMerchantConfigValue } from '@tectonics/prs/merchant/config/get-merchant-config-value';
import {
  getSafeDueDate,
  usePendingIncomingPayment,
  getMerchant,
} from '@tectonics/prs/incoming-payment';
import { PagofacilInformation } from './pagofacil-information';
import { ShowBarcodePdfButton } from '../commons/show-barcode-pdf-button';
import { DisplayBarcodeNumber } from '../commons/display-barcode-number';
import { ButtonWrapper, VariableWidthDiv } from '../commons/render-helpers';
import { VoucherNumberBox } from '../commons/voucher-number-box';
import { VoucherText } from '../commons/voucher/voucher-text';
import { Separator } from '../../../atoms';
import { DisplayDueDate } from '../../../organisms/display-due-date';
import { DisplayTotalAmount } from '../../../organisms/display-total-amount';
import {
  DashedHr,
  PaymentInfoWrapper,
} from '../../../organisms/shared-styled-components';
import { RenderPaymentInfoContainer } from '../../../organisms/payment-info-display';
import DisplayTotalAmountWithTaxesTip from './display-total-amount-with-taxes-tip';
import { useScreenWidth } from '@tectonics/hooks';
import phoneTipSrc from './phone-tip.svg';

export function RenderTotalDueDateAndBarCode(): Node {
  const incomingPayment = usePendingIncomingPayment();
  return (
    <>
      <PaymentInfoWrapper>
        <HandleDisplayTotalAmount label="Valor a pagar" location="left" redesign={true} />
        <DisplayDueDate label="Fecha de Vencimiento" sublabel={`Pagar hasta el ${getWeekDay(getSafeDueDate(incomingPayment), dueDateWeekDay)}`} redesign={true} />
        <VoucherNumberBox redesign={true}>
          <br/>
          <RenderVoucherText />
          <br />
          <DisplayBarcodeNumber redesign={true} />
          <br />
        </VoucherNumberBox>
      </PaymentInfoWrapper>
      <Separator direction="vertical" size={3} />
    </>
  );
}

export function HandleDisplayTotalAmount(): Node {
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 840;

  if (isMobile) {
    return (
      <DisplayTotalAmountWithTaxesTip label="Valor a pagar" location="left" redesign={true} />
    );
  }

  return (
    <DisplayTotalAmount label="Valor a pagar" location="left" redesign={true} />
  );
}

export function RenderButtonArea(): Node {
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 840;
  if (isMobile) {
    return (
      <>
        <ButtonWrapper>
          <ShowBarcodePdfButton inverseStyle={true} format="pdf" buttonMessage="Guardar instrucciones en pdf" />
        </ButtonWrapper>
      </>
    );
  }

  return (
    <>
      <ButtonWrapper>
        <ShowBarcodePdfButton format="pdf" buttonMessage="Guardar instrucciones en pdf" />
      </ButtonWrapper>
      <ButtonWrapper>
        <ShowBarcodePdfButton inverseStyle={true} buttonMessage="Imprimir instrucciones" />
      </ButtonWrapper>
    </>
  );
}

export function RenderPaymentInstructions(): Node {
  return (
    <>
      <DashedHr />
      <PagofacilInformation />
      <DashedHr />

      <RenderPaymentInfoContainer
        bgColor="#ffffff"
        h1Text="Instrucciones de pago"
        items={[
          {
            imgSrc: 'https://cdn.ebanx.com/images/vouchers/cobro-icon.svg',
            subText: 'Dicta el número de pago correctamente al cajero PagoFácil para que la operación sea realizada con éxito.',
          },
          {
            imgSrc: phoneTipSrc,
            subText: 'La confirmación se realizará instantes después de completar el pago.',
          },
        ]}
      />

      <DashedHr />

      <RenderPaymentInfoContainer
        h1Text="¿Ya conoces la Central de Ayuda EBANX?"
        items={[
          {
            imgSrc: 'https://cdn.ebanx.com/images/vouchers/files-icon.svg',
            subText: 'Si tienes dudas adicionales acerca de tu pago, o tu reembolso visítanos en la Central de Ayuda EBANX:',
            strong: 'ayuda.ebanx.com/hc/es-ar',
          },
        ]}
      />
    </>
  );
}

export function RenderVoucherText(): Node {
  const incomingPayment = usePendingIncomingPayment();
  const merchant = getMerchant(incomingPayment);
  const ebanx_silver_enabled = getMerchantConfigValue('ebanx_silver_enabled', merchant);

  let business_name = 'EBANX';

  if (ebanx_silver_enabled === true) {
    business_name = 'ZILVER';
  }

  return (
    <>
      <VoucherText><b>Datos de pago</b></VoucherText>
      <br />
      <VariableWidthDiv width="90">Indica al cajero que tienes un <b>servicio sin factura de {business_name}</b> y dicta los números que figuran a continuación.</VariableWidthDiv>
    </>
  );
}

const dueDateWeekDay = [
  'lunes',
  'lunes',
  'martes',
  'miércoles',
  'jueves',
  'viernes',
  'lunes',
];
